import { useState, useEffect } from "react";
import { useAuthContext } from "../../contexts/AuthContext";
import { signOut, updatePassword, EmailAuthProvider, linkWithCredential } from "firebase/auth";
import { auth, db } from "../../lib/firebase";
import { doc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { User, Mail, Save, LogOut, Loader2, Lock, Eye, EyeOff, ShieldCheck } from "lucide-react";

export default function ProfilePage() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState("");
  
  // Password linking for Gmail users
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [linkingPassword, setLinkingPassword] = useState(false);
  const [linkSuccess, setLinkSuccess] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [hasPasswordProvider, setHasPasswordProvider] = useState(false);
  const [isGoogleUser, setIsGoogleUser] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || user.email?.split("@")[0] || "");
      
      // Check if user has password provider
      const currentUser = auth.currentUser;
      if (currentUser) {
        const providers = currentUser.providerData.map(p => p.providerId);
        setHasPasswordProvider(providers.includes('password'));
        setIsGoogleUser(providers.includes('google.com'));
      }
    }
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    
    if (!name.trim()) {
      setError("กรุณากรอกชื่อผู้ใช้");
      return;
    }

    setSaving(true);
    setError("");
    setSaveSuccess(false);

    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        name: name.trim(),
        displayName: name.trim(),
      });

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error("Error updating profile:", err);
      setError("ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setSaving(false);
    }
  };

  const handleLinkPassword = async () => {
    if (!auth.currentUser) return;
    
    if (!newPassword || !confirmNewPassword) {
      setLinkError("กรุณากรอกรหัสผ่านให้ครบถ้วน");
      return;
    }
    
    if (newPassword.length < 6) {
      setLinkError("รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร");
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      setLinkError("รหัสผ่านไม่ตรงกัน");
      return;
    }

    setLinkingPassword(true);
    setLinkError("");
    setLinkSuccess(false);

    try {
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email!,
        newPassword
      );
      
      await linkWithCredential(auth.currentUser, credential);
      
      setLinkSuccess(true);
      setHasPasswordProvider(true);
      setNewPassword("");
      setConfirmNewPassword("");
      setTimeout(() => setLinkSuccess(false), 5000);
    } catch (err: any) {
      console.error("Error linking password:", err);
      
      let errorMessage = "ไม่สามารถตั้งรหัสผ่านได้ กรุณาลองใหม่อีกครั้ง";
      
      if (err.code === 'auth/provider-already-linked') {
        errorMessage = "บัญชีนี้มีรหัสผ่านอยู่แล้ว";
      } else if (err.code === 'auth/weak-password') {
        errorMessage = "รหัสผ่านไม่ปลอดภัยเพียงพอ";
      }
      
      setLinkError(errorMessage);
    } finally {
      setLinkingPassword(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">🔐</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">กรุณาเข้าสู่ระบบ</h2>
          <p className="text-gray-600 mb-6">เพื่อดูโปรไฟล์ของคุณ</p>
          <button
            onClick={() => navigate("/login")}
            className="bg-gradient-primary text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105"
          >
            เข้าสู่ระบบ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-soft py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">โปรไฟล์ของฉัน</h1>
          <p className="text-gray-600">จัดการข้อมูลส่วนตัวของคุณ</p>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 mb-6">
          {/* Avatar Section */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-24 h-24 bg-gradient-primary rounded-full flex items-center justify-center text-white text-4xl font-bold shadow-lg mb-4">
              {user.email?.[0].toUpperCase()}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-semibold">
                {user.role === "instructor" ? "ผู้สอน" : "ผู้เรียน"}
              </span>
            </div>
          </div>

          {/* Form Section */}
          <div className="space-y-6">
            {/* Name Input */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <User className="w-4 h-4" />
                ชื่อผู้ใช้
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="กรอกชื่อของคุณ"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors"
              />
            </div>

            {/* Email Display (Read-only) */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <Mail className="w-4 h-4" />
                อีเมล
              </label>
              <input
                type="email"
                value={user.email}
                readOnly
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-600 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">ไม่สามารถแก้ไขอีเมลได้</p>
            </div>

            {/* Password Linking Section for Gmail Users */}
            {isGoogleUser && !hasPasswordProvider && (
              <div className="border-2 border-blue-200 rounded-xl p-6 bg-blue-50">
                <div className="flex items-start gap-3 mb-4">
                  <ShieldCheck className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-800 mb-1">ตั้งรหัสผ่านสำหรับบัญชี Gmail</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      คุณสมัครด้วย Gmail ตั้งรหัสผ่านเพื่อให้สามารถ Login ด้วย Email/Password ได้
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* New Password */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <Lock className="w-4 h-4" />
                      รหัสผ่านใหม่
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="อย่างน้อย 6 ตัวอักษร"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <Lock className="w-4 h-4" />
                      ยืนยันรหัสผ่าน
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        placeholder="กรอกรหัสผ่านอีกครั้ง"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Link Success Message */}
                  {linkSuccess && (
                    <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                      <p className="text-green-700 text-sm font-semibold">✓ ตั้งรหัสผ่านสำเร็จ! ตอนนี้คุณสามารถ Login ด้วย Email/Password ได้แล้ว</p>
                    </div>
                  )}

                  {/* Link Error Message */}
                  {linkError && (
                    <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
                      <p className="text-red-700 text-sm">{linkError}</p>
                    </div>
                  )}

                  {/* Link Password Button */}
                  <button
                    onClick={handleLinkPassword}
                    disabled={linkingPassword}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {linkingPassword ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        กำลังตั้งรหัสผ่าน...
                      </>
                    ) : (
                      <>
                        <Lock className="w-5 h-5" />
                        ตั้งรหัสผ่าน
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Show success message if password is already linked */}
            {isGoogleUser && hasPasswordProvider && (
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-2 text-green-700">
                  <ShieldCheck className="w-5 h-5" />
                  <p className="text-sm font-semibold">✓ บัญชีของคุณสามารถ Login ด้วย Gmail และ Email/Password ได้แล้ว</p>
                </div>
              </div>
            )}

            {/* Success Message */}
            {saveSuccess && (
              <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                <p className="text-green-700 text-sm font-semibold">✓ บันทึกข้อมูลสำเร็จ</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-gradient-primary text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  กำลังบันทึก...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  บันทึกข้อมูล
                </>
              )}
            </button>
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="w-full bg-red-100 text-red-700 py-4 rounded-xl font-semibold hover:bg-red-200 transition-all flex items-center justify-center gap-2"
        >
          <LogOut className="w-5 h-5" />
          ออกจากระบบ
        </button>
      </div>
    </div>
  );
}
